import { useState, useEffect, useCallback } from "react";

import {
  Collapse,
  Text,
  Button,
  Input,
  Grid,
  useInput,
  Loading,
} from "@nextui-org/react";
import { UserIcon } from "./UserIcon";
import ReactJson from "react-json-view";
import ResultsInfo from "../auth/ResultsInfo";
import { useNotifications } from "reapop";
import { makeFetch } from "../../service/makeFetch";
import AceEditor from "react-ace";
import useStorage from "../storage/useLocalStorage";

const FindAccountById = ({ setData = () => null }) => {
  const idTypeInput = useInput("");
  const idInput = useInput("");
  const { notify } = useNotifications();

  const findAccount = useCallback(async () => {
    if (
      idTypeInput.value === undefined ||
      idTypeInput.value === null ||
      idTypeInput.value === ""
    )
      return notify("Error please provide idType!", "error");
    if (
      idInput.value === undefined ||
      idInput.value === null ||
      idInput.value === ""
    )
      return notify("Error please provide id!", "error");
    const result = await makeFetch({
      url: `account/find/${idTypeInput.value}/${idInput.value}`,
      method: "GET",
      privateCall: false,
    });
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, [idTypeInput.value, idInput.value]);
  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <Text b> Method: GET</Text>
        <Grid.Container gap={2} style={{ marginTop: 20 }}>
          <Grid>
            <Input
              bordered
              labelPlaceholder="idType"
              color="primary"
              {...idTypeInput.bindings}
              clearable
            />
          </Grid>
          <Grid>
            <Input
              bordered
              labelPlaceholder="id"
              color="primary"
              clearable
              {...idInput.bindings}
            />
          </Grid>
        </Grid.Container>
      </div>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onClick={findAccount}
      >
        Find Account
      </Button>
    </div>
  );
};

const UpdateAccount = ({ setData = () => null, isDeveloper = false }) => {
  const jsonInput = useInput({
    userId: "",
    itemDetailsAdd: {},
    itemDetailsUpdate: {},
  });
  const { notify } = useNotifications();

  const handleChange = useCallback((text) => {
    try {
      jsonInput.setValue(text && JSON.parse(text));
    } catch (error) {}
  }, []);

  const updateAccount = useCallback(async () => {
    const url = isDeveloper === true ? `account/dev/update` : `account/update`;
    console.log("jsonInput", jsonInput);
    const result = await makeFetch({
      url,
      method: "POST",
      privateCall: false,
      body: jsonInput.value,
    });
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, [jsonInput.value]);
  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text b> Method: POST</Text>
          <Text b>Editor:</Text>
        </div>
        <Grid.Container gap={2} style={{ marginTop: 20 }}>
          <AceEditor
            mode="java"
            theme="github"
            onChange={handleChange}
            name="updateAccount"
            editorProps={{ $blockScrolling: true }}
            value={JSON.stringify(jsonInput.value, null, 2)}
          />
        </Grid.Container>
      </div>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onPress={updateAccount}
      >
        Update Account
      </Button>
    </div>
  );
};

const CreateAccount = ({ setData = () => null }) => {
  const jsonInput = useInput({
    idType: "",
    googleId: "",
    appleId: "",
    facebookId: "",
    walletAddress: "",
    handle: "",
    email: "",
    password: "",
    authActionCreateAccountKey: "",
    developerPlayGroundKey: "",
    createAccountWhenNotFound: true,
    referralsId: "",
  });
  const { notify } = useNotifications();

  const handleChange = useCallback((text) => {
    try {
      jsonInput.setValue(text && JSON.parse(text));
    } catch (error) {}
  }, []);

  const createAccount = useCallback(async () => {
    const result = await makeFetch({
      url: `account/create`,
      method: "POST",
      privateCall: false,
      body: jsonInput.value,
    });
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, [jsonInput.value]);
  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text b> Method: POST</Text>
          <Text b>Editor:</Text>
        </div>
        <Grid.Container gap={2} style={{ marginTop: 20 }}>
          <AceEditor
            mode="java"
            theme="github"
            onChange={handleChange}
            name="createAccount"
            editorProps={{ $blockScrolling: true }}
            value={JSON.stringify(jsonInput.value, null, 2)}
          />
        </Grid.Container>
      </div>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onPress={createAccount}
      >
        Create Account
      </Button>
    </div>
  );
};

const GetAccountAndDetails = ({
  setData = () => null,
  isDeveloper = false,
}) => {
  const jsonInput = useInput({
    userId: null,
    email: null,
    keyNames: "handle,developerKey,friends,firstName",
  });
  const { notify } = useNotifications();

  const handleChange = useCallback((text) => {
    try {
      jsonInput.setValue(text && JSON.parse(text));
    } catch (error) {}
  }, []);

  const findDetails = useCallback(async () => {
    const url =
      isDeveloper === true
        ? `account/dev/find/details`
        : `account/find/details`;
    const result = await makeFetch({
      url,
      method: "POST",
      privateCall: false,
      body: jsonInput.value,
    });
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, [jsonInput.value]);
  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text b> Method: POST</Text>
          <Text b>Editor:</Text>
        </div>
        <Grid.Container gap={2} style={{ marginTop: 20 }}>
          <AceEditor
            mode="java"
            theme="github"
            onChange={handleChange}
            name="getAccountAndDetails"
            editorProps={{ $blockScrolling: true }}
            value={JSON.stringify(jsonInput.value, null, 2)}
          />
        </Grid.Container>
      </div>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onPress={findDetails}
      >
        Find Account
      </Button>
    </div>
  );
};

const GetAuthorizationToken = ({ setData = () => null }) => {
  const jsonInput = useInput({
    userId: null,
    developerId: null,
    developerKey: null,
    developerAccessKey: null,
  });
  const { notify } = useNotifications();

  const handleChange = useCallback((text) => {
    try {
      jsonInput.setValue(text && JSON.parse(text));
    } catch (error) {}
  }, []);

  const getData = useCallback(async () => {
    const result = await makeFetch({
      url: `account/token`,
      method: "POST",
      privateCall: false,
      body: jsonInput.value,
    });
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, [jsonInput.value]);
  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text b> Method: POST</Text>
          <Text b>Editor:</Text>
        </div>
        <Grid.Container gap={2} style={{ marginTop: 20 }}>
          <AceEditor
            mode="java"
            theme="github"
            onChange={handleChange}
            name="getAuthorizationToken"
            editorProps={{ $blockScrolling: true }}
            value={JSON.stringify(jsonInput.value, null, 2)}
          />
        </Grid.Container>
      </div>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onPress={getData}
      >
        Get Token
      </Button>
    </div>
  );
};

const GetExistingHandles = ({ setData = () => null }) => {
  const jsonInput = useInput({
    handles: [],
  });
  const { notify } = useNotifications();

  const handleChange = useCallback((text) => {
    try {
      jsonInput.setValue(text && JSON.parse(text));
    } catch (error) {}
  }, []);

  const getData = useCallback(async () => {
    const result = await makeFetch({
      url: `account/handles/exist`,
      method: "POST",
      privateCall: false,
      body: jsonInput.value,
    });
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, [jsonInput.value]);

  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text b> Method: POST</Text>
          <Text b>Editor:</Text>
        </div>
        <Grid.Container gap={1} style={{ marginTop: 20 }}>
          <AceEditor
            mode="java"
            theme="github"
            onChange={handleChange}
            name="getExistingHandles"
            editorProps={{ $blockScrolling: true }}
            value={JSON.stringify(jsonInput.value, null, 2)}
            style={{ height: 100 }}
          />
        </Grid.Container>
      </div>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onPress={getData}
      >
        Get Handles
      </Button>
    </div>
  );
};

const GetUsersReferrals = ({ setData = () => null }) => {
  const idInput = useInput("");
  const { notify } = useNotifications();

  const getData = useCallback(async () => {
    if (
      idInput.value === undefined ||
      idInput.value === null ||
      idInput.value === ""
    )
      return notify("Error please provide userId!", "error");
    const result = await makeFetch({
      url: `account/referrals/${idInput.value}`,
      method: "GET",
      privateCall: false,
    });
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, [idInput.value]);
  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <Text b> Method: GET</Text>
        <Grid.Container gap={1} style={{ marginTop: 20 }}>
          <Grid>
            <Input
              bordered
              labelPlaceholder="id"
              color="primary"
              clearable
              {...idInput.bindings}
            />
          </Grid>
        </Grid.Container>
      </div>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onClick={getData}
      >
        Get Referrals
      </Button>
    </div>
  );
};

const UpdateStats = ({ setData = () => null }) => {
  const jsonInput = useInput({
    userId: null,
    statsToUpdate: {
      xp: { operation: "increment", value: 15 },
      energy: { operation: "decrement", value: 10 },
      happiness: { operation: "increment", value: 5 },
      social: { operation: "increment", value: 50 },
      health: { operation: "decrement", value: 100 },
    },
  });
  const { notify } = useNotifications();

  const handleChange = useCallback((text) => {
    try {
      jsonInput.setValue(text && JSON.parse(text));
    } catch (error) {}
  }, []);

  const updatestats = useCallback(async () => {
    const result = await makeFetch({
      url: `account/stats/update`,
      method: "POST",
      privateCall: false,
      body: jsonInput.value,
    });
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, [jsonInput.value]);

  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text b> Method: POST</Text>
          <Text b>Editor:</Text>
        </div>
        <Grid.Container gap={2} style={{ marginTop: 20 }}>
          <AceEditor
            mode="java"
            theme="github"
            onChange={handleChange}
            name="updateStats"
            editorProps={{ $blockScrolling: true }}
            value={JSON.stringify(jsonInput.value, null, 2)}
          />
        </Grid.Container>
      </div>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onPress={updatestats}
      >
        Update Stats
      </Button>
    </div>
  );
};

const GetStats = ({ setData = () => null }) => {
  const userIdInput = useInput("");
  const { notify } = useNotifications();

  const getStats = useCallback(async () => {
    if (
      userIdInput.value === undefined ||
      userIdInput.value === null ||
      userIdInput.value === ""
    )
      return notify("Error please provide userId!", "error");
    const result = await makeFetch({
      url: `account/stats/${userIdInput.value}`,
      method: "GET",
      privateCall: false,
    });
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, [userIdInput.value]);

  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <Text b> Method: GET</Text>
      </div>
      <Grid.Container gap={2} style={{ marginTop: 20 }}>
        <Grid>
          <Input
            bordered
            labelPlaceholder="userId"
            color="primary"
            {...userIdInput.bindings}
            clearable
          />
        </Grid>
      </Grid.Container>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onClick={getStats}
      >
        Get Stats
      </Button>
    </div>
  );
};

const GetUsersScene = ({ setData = () => null }) => {
  const userIdInput = useInput("");
  const sceneIdInput = useInput("");
  const { notify } = useNotifications();

  const getScene = useCallback(async () => {
    if (
      userIdInput.value === undefined ||
      userIdInput.value === null ||
      userIdInput.value === ""
    )
      return notify("Error please provide userId!", "error");
    if (
      sceneIdInput.value === undefined ||
      sceneIdInput.value === null ||
      sceneIdInput.value === ""
    )
      return notify("Error please scene id!", "error");
    const result = await makeFetch({
      url: `scenes/find/${userIdInput.value}/${sceneIdInput.value}`,
      method: "GET",
      privateCall: false,
    });
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, [userIdInput.value, sceneIdInput.value]);

  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text b> Method: GET</Text>
        </div>
        <Grid.Container gap={2} style={{ marginTop: 20 }}>
          <Grid>
            <Input
              bordered
              labelPlaceholder="userId"
              color="primary"
              {...userIdInput.bindings}
              clearable
            />
          </Grid>
          <Grid>
            <Input
              bordered
              labelPlaceholder="sceneId"
              color="primary"
              clearable
              {...sceneIdInput.bindings}
            />
          </Grid>
        </Grid.Container>
      </div>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onClick={getScene}
      >
        Get Scene
      </Button>
    </div>
  );
};

const GetAllUsersScene = ({ setData = () => null }) => {
  const userIdInput = useInput("");
  const { notify } = useNotifications();

  const getAllScene = useCallback(async () => {
    if (
      userIdInput.value === undefined ||
      userIdInput.value === null ||
      userIdInput.value === ""
    )
      return notify("Error please provide userId!", "error");
    const result = await makeFetch({
      url: `scenes/all/${userIdInput.value}`,
      method: "GET",
      privateCall: false,
    });
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, [userIdInput.value]);

  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text b> Method: POST</Text>
        </div>
        <Grid.Container gap={2} style={{ marginTop: 20 }}>
          <Grid>
            <Input
              bordered
              labelPlaceholder="userId"
              color="primary"
              {...userIdInput.bindings}
              clearable
            />
          </Grid>
        </Grid.Container>
      </div>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onClick={getAllScene}
      >
        Get Scenes
      </Button>
    </div>
  );
};

const DeleteScene = ({ setData = () => null }) => {
  const sceneIdInput = useInput("");
  const { notify } = useNotifications();

  const getLand = useCallback(async () => {
    if (
      sceneIdInput.value === undefined ||
      sceneIdInput.value === null ||
      sceneIdInput.value === ""
    )
      return notify("Error please provide secene id!", "error");

    const result = await makeFetch({
      url: `scene/delete/${sceneIdInput.value}`,
      method: "GET",
      privateCall: false,
    });
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, [sceneIdInput.value]);

  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text b> Method: GET</Text>
        </div>
        <Grid.Container gap={2} style={{ marginTop: 20 }}>
          <Grid>
            <Input
              bordered
              labelPlaceholder="SceneId"
              color="primary"
              {...sceneIdInput.bindings}
              clearable
            />
          </Grid>
        </Grid.Container>
      </div>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onClick={getLand}
      >
        Delete Scene
      </Button>
    </div>
  );
};

const CreateScene = ({ setData = () => null }) => {
  const jsonInput = useInput({
    userId: "641a861ccc0c49e524ab5d43",
    name: "just a scene 1",
    description: " just a description scene 1",
  });
  const { notify } = useNotifications();

  const handleChange = useCallback((text) => {
    try {
      jsonInput.setValue(text && JSON.parse(text));
    } catch (error) {}
  }, []);

  const createAsset = useCallback(async () => {
    const result = await makeFetch({
      url: `scenes/create`,
      method: "POST",
      privateCall: false,
      body: jsonInput.value,
    });
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, [jsonInput.value]);
  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text b> Method: POST</Text>
          <Text b>Editor:</Text>
        </div>
        <Grid.Container gap={2} style={{ marginTop: 20 }}>
          <AceEditor
            mode="java"
            theme="github"
            onChange={handleChange}
            name="createScene"
            editorProps={{ $blockScrolling: true }}
            value={JSON.stringify(jsonInput.value, null, 2)}
          />
        </Grid.Container>
      </div>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onPress={createAsset}
      >
        Create Scene
      </Button>
    </div>
  );
};

const sceneUpdateEdittor = {
  sceneId: "",
  itemDetailsAdd: {
    owners: ["6424ea8fc68544a497de2942"],
  },
  itemDetailsUpdate: {
    name: "",
  },
};

const UpdateScene = ({ setData = () => null }) => {
  const jsonInput = useInput(sceneUpdateEdittor);
  const { notify } = useNotifications();

  const handleChange = useCallback((text) => {
    try {
      jsonInput.setValue(text && JSON.parse(text));
    } catch (error) {}
  }, []);

  const toupdateScene = useCallback(async () => {
    const result = await makeFetch({
      url: `scene/update`,
      method: "POST",
      privateCall: false,
      body: jsonInput.value,
    });
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, [jsonInput.value]);

  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text b> Method: POST</Text>
          <Text b>Editor:</Text>
        </div>
        <Grid.Container gap={2} style={{ marginTop: 20 }}>
          <AceEditor
            mode="java"
            theme="github"
            onChange={handleChange}
            name="updateScene"
            editorProps={{ $blockScrolling: true }}
            value={JSON.stringify(jsonInput.value, null, 2)}
          />
        </Grid.Container>
      </div>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onPress={toupdateScene}
      >
        Update Scene
      </Button>
    </div>
  );
};

const GetAsset = ({ setData = () => null, checkOwner = true }) => {
  const userIdInput = useInput("");
  const assetIdInput = useInput("");
  const { notify } = useNotifications();

  const getAsset = useCallback(async () => {
    if (
      (userIdInput.value === undefined ||
        userIdInput.value === null ||
        userIdInput.value === "") &&
      checkOwner === true
    )
      return notify("Error please provide userId!", "error");
    if (
      assetIdInput.value === undefined ||
      assetIdInput.value === null ||
      assetIdInput.value === ""
    )
      return notify("Error please asset id!", "error");
    var url =
      checkOwner === true
        ? `${userIdInput.value}/${assetIdInput.value}`
        : `${assetIdInput.value}`;
    const result = await makeFetch({
      url: `assets/find/${url}/`,
      method: "GET",
      privateCall: false,
    });
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, [userIdInput.value, assetIdInput.value]);
  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text b> Method: GET</Text>
        </div>
        <Grid.Container gap={2} style={{ marginTop: 20 }}>
          {checkOwner === true && (
            <Grid>
              <Input
                bordered
                labelPlaceholder="userId"
                color="primary"
                {...userIdInput.bindings}
                clearable
              />
            </Grid>
          )}
          <Grid>
            <Input
              bordered
              labelPlaceholder="assetId"
              color="primary"
              clearable
              {...assetIdInput.bindings}
            />
          </Grid>
        </Grid.Container>
      </div>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onPress={getAsset}
      >
        Get Asset
      </Button>
    </div>
  );
};

const assetEdditor = {
  url: "https://instantestore.com/images/logo.png",
  name: "a car asset",
  category: ["car"],
  assetPrice: "1000",
  limit: 10,
  owners: ["641a861ccc0c49e524ab5d43"],
};
const CreateAsset = ({ setData = () => null }) => {
  const jsonInput = useInput(assetEdditor);
  const { notify } = useNotifications();

  const handleChange = useCallback((text) => {
    try {
      jsonInput.setValue(text && JSON.parse(text));
    } catch (error) {}
  }, []);

  const createAsset = useCallback(async () => {
    const result = await makeFetch({
      url: `assets/create`,
      method: "POST",
      privateCall: false,
      body: jsonInput.value,
    });
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, [jsonInput.value]);
  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text b> Method: POST</Text>
          <Text b>Editor:</Text>
        </div>
        <Grid.Container gap={2} style={{ marginTop: 20 }}>
          <AceEditor
            mode="java"
            theme="github"
            onChange={handleChange}
            name="createAsset"
            editorProps={{ $blockScrolling: true }}
            value={JSON.stringify(jsonInput.value, null, 2)}
          />
        </Grid.Container>
      </div>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onPress={createAsset}
      >
        Create Asset
      </Button>
    </div>
  );
};
const assetBuyEdditor = {
  userId: "",
  assetId: "",
};
const BuyAsset = ({ setData = () => null }) => {
  const jsonInput = useInput(assetBuyEdditor);
  const { notify } = useNotifications();

  const handleChange = useCallback((text) => {
    try {
      jsonInput.setValue(text && JSON.parse(text));
    } catch (error) {}
  }, []);

  const buyAsset = useCallback(async () => {
    const result = await makeFetch({
      url: `asset/buy`,
      method: "POST",
      privateCall: false,
      body: jsonInput.value,
    });
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, [jsonInput.value]);
  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text b> Method: POST</Text>
          <Text b>Editor:</Text>
        </div>
        <Grid.Container gap={2} style={{ marginTop: 20 }}>
          <AceEditor
            mode="java"
            theme="github"
            onChange={handleChange}
            name="buyAsset"
            editorProps={{ $blockScrolling: true }}
            value={JSON.stringify(jsonInput.value, null, 2)}
          />
        </Grid.Container>
      </div>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onPress={buyAsset}
      >
        Buy Asset
      </Button>
    </div>
  );
};

const DeleteAsset = ({ setData = () => null }) => {
  const assetIdInput = useInput("");
  const { notify } = useNotifications();

  const getLand = useCallback(async () => {
    if (
      assetIdInput.value === undefined ||
      assetIdInput.value === null ||
      assetIdInput.value === ""
    )
      return notify("Error please provide asset key!", "error");

    const result = await makeFetch({
      url: `asset/delete/${assetIdInput.value}`,
      method: "GET",
      privateCall: false,
    });
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, [assetIdInput.value]);

  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text b> Method: GET</Text>
        </div>
        <Grid.Container gap={2} style={{ marginTop: 20 }}>
          <Grid>
            <Input
              bordered
              labelPlaceholder="asset key"
              color="primary"
              {...assetIdInput.bindings}
              clearable
            />
          </Grid>
        </Grid.Container>
      </div>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onClick={getLand}
      >
        Delete Asset
      </Button>
    </div>
  );
};

const assetUpdateEdittor = {
  assetId: "",
  itemDetailsAdd: {
    owners: ["6424ea8fc68544a497de2942"],
  },
  itemDetailsUpdate: {
    assetPrice: "",
    limit: 0,
  },
};

const UpdateAsset = ({ setData = () => null }) => {
  const jsonInput = useInput(assetUpdateEdittor);
  const { notify } = useNotifications();

  const handleChange = useCallback((text) => {
    try {
      jsonInput.setValue(text && JSON.parse(text));
    } catch (error) {}
  }, []);

  const toupdateAsset = useCallback(async () => {
    const result = await makeFetch({
      url: `asset/update`,
      method: "POST",
      privateCall: false,
      body: jsonInput.value,
    });
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, [jsonInput.value]);

  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text b> Method: POST</Text>
          <Text b>Editor:</Text>
        </div>
        <Grid.Container gap={2} style={{ marginTop: 20 }}>
          <AceEditor
            mode="java"
            theme="github"
            onChange={handleChange}
            name="updateAsset"
            editorProps={{ $blockScrolling: true }}
            value={JSON.stringify(jsonInput.value, null, 2)}
          />
        </Grid.Container>
      </div>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onPress={toupdateAsset}
      >
        Update Asset
      </Button>
    </div>
  );
};

const GetAllUsersAssets = ({ setData = () => null, checkOwner = true }) => {
  const userIdInput = useInput("");
  const { notify } = useNotifications();

  const getAllAsset = useCallback(async () => {
    if (
      (userIdInput.value === undefined ||
        userIdInput.value === null ||
        userIdInput.value === "") &&
      checkOwner === true
    )
      return notify("Error please provide userId!", "error");
    var url =
      checkOwner === true ? `assets/all/${userIdInput.value}` : `assets/all`;
    const result = await makeFetch({
      url,
      method: "GET",
      privateCall: false,
    });
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, [userIdInput.value]);

  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text b> Method: GET</Text>
        </div>
        {checkOwner === true && (
          <Grid.Container gap={2} style={{ marginTop: 20 }}>
            <Grid>
              <Input
                bordered
                labelPlaceholder="userId"
                color="primary"
                {...userIdInput.bindings}
                clearable
              />
            </Grid>
          </Grid.Container>
        )}
      </div>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onPress={getAllAsset}
      >
        Get Assets
      </Button>
    </div>
  );
};

const landEditor = {
  landId: "A223",
  name: "land 1",
  userId: "",
  description: "land 2 2023",
  assets: [
    {
      assetId: "643da29f9523c40e4fa101f2",
      position: "top",
      rotation: "",
    },
  ],
  rotation: "",
  landPrice: 12,
  landSize: 3000,
};
const CreateLand = ({ setData = () => null }) => {
  const jsonInput = useInput(landEditor);
  const { notify } = useNotifications();

  const handleChange = useCallback((text) => {
    try {
      jsonInput.setValue(text && JSON.parse(text));
    } catch (error) {}
  }, []);

  const createLand = useCallback(async () => {
    const result = await makeFetch({
      url: `lands/create`,
      method: "POST",
      privateCall: false,
      body: jsonInput.value,
    });
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, [jsonInput.value]);
  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text b> Method: POST</Text>
          <Text b>Editor:</Text>
        </div>
        <Grid.Container gap={2} style={{ marginTop: 20 }}>
          <AceEditor
            mode="java"
            theme="github"
            onChange={handleChange}
            name="createLand"
            editorProps={{ $blockScrolling: true }}
            value={JSON.stringify(jsonInput.value, null, 2)}
          />
        </Grid.Container>
      </div>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onPress={createLand}
      >
        Create Land
      </Button>
    </div>
  );
};

const landBuyEdditor = {
  userId: "",
  landId: "",
};
const BuyLand = ({ setData = () => null }) => {
  const jsonInput = useInput(landBuyEdditor);
  const { notify } = useNotifications();

  const handleChange = useCallback((text) => {
    try {
      jsonInput.setValue(text && JSON.parse(text));
    } catch (error) {}
  }, []);

  const buyLand = useCallback(async () => {
    const result = await makeFetch({
      url: `lands/buy`,
      method: "POST",
      privateCall: false,
      body: jsonInput.value,
    });
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, [jsonInput.value]);
  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text b> Method: POST</Text>
          <Text b>Editor:</Text>
        </div>
        <Grid.Container gap={2} style={{ marginTop: 20 }}>
          <AceEditor
            mode="java"
            theme="github"
            onChange={handleChange}
            name="buyLand"
            editorProps={{ $blockScrolling: true }}
            value={JSON.stringify(jsonInput.value, null, 2)}
          />
        </Grid.Container>
      </div>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onPress={buyLand}
      >
        Buy Land
      </Button>
    </div>
  );
};
const addAssetsToLandEditor = {
  landId: "A223",
  assets: [
    { assetId: "643da29f9523c40e4fa101f2", position: "top", rotation: "" },
  ],
};

const AddAssetsToLand = ({ setData = () => null }) => {
  const jsonInput = useInput(addAssetsToLandEditor);
  const { notify } = useNotifications();

  const handleChange = useCallback((text) => {
    try {
      jsonInput.setValue(text && JSON.parse(text));
    } catch (error) {}
  }, []);

  const addAssets = useCallback(async () => {
    const result = await makeFetch({
      url: `lands/assets/add`,
      method: "POST",
      privateCall: false,
      body: jsonInput.value,
    });
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, [jsonInput.value]);
  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text b> Method: POST</Text>
          <Text b>Editor:</Text>
        </div>
        <Grid.Container gap={2} style={{ marginTop: 20 }}>
          <AceEditor
            mode="java"
            theme="github"
            onChange={handleChange}
            name="addAssets"
            editorProps={{ $blockScrolling: true }}
            value={JSON.stringify(jsonInput.value, null, 2)}
          />
        </Grid.Container>
      </div>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onPress={addAssets}
      >
        Add Asset
      </Button>
    </div>
  );
};

const GetLand = ({ setData = () => null, checkOwner = true }) => {
  const userIdInput = useInput("");
  const landIdInput = useInput("");
  const { notify } = useNotifications();

  const getLand = useCallback(async () => {
    if (
      (userIdInput.value === undefined ||
        userIdInput.value === null ||
        userIdInput.value === "") &&
      checkOwner === true
    )
      return notify("Error please provide userId!", "error");
    if (
      landIdInput.value === undefined ||
      landIdInput.value === null ||
      landIdInput.value === ""
    )
      return notify("Error please land id!", "error");
    var url =
      checkOwner === true
        ? `${userIdInput.value}/${landIdInput.value}`
        : `${landIdInput.value}`;
    const result = await makeFetch({
      url: `lands/find/${url}`,
      method: "GET",
      privateCall: false,
    });
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, [userIdInput.value, landIdInput.value]);

  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text b> Method: GET</Text>
        </div>
        <Grid.Container gap={2} style={{ marginTop: 20 }}>
          {checkOwner === true && (
            <Grid>
              <Input
                bordered
                labelPlaceholder="userId"
                color="primary"
                {...userIdInput.bindings}
                clearable
              />
            </Grid>
          )}
          <Grid>
            <Input
              bordered
              labelPlaceholder="landId"
              color="primary"
              clearable
              {...landIdInput.bindings}
            />
          </Grid>
        </Grid.Container>
      </div>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onClick={getLand}
      >
        Get Land
      </Button>
    </div>
  );
};

const GetUsersLands = ({ setData = () => null }) => {
  const userIdInput = useInput("");
  const { notify } = useNotifications();

  const getLand = useCallback(async () => {
    if (
      userIdInput.value === undefined ||
      userIdInput.value === null ||
      userIdInput.value === ""
    )
      return notify("Error please provide userId!", "error");

    const result = await makeFetch({
      url: `lands/all/${userIdInput.value}`,
      method: "GET",
      privateCall: false,
    });
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, [userIdInput.value]);

  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text b> Method: GET</Text>
        </div>
        <Grid.Container gap={2} style={{ marginTop: 20 }}>
          <Grid>
            <Input
              bordered
              labelPlaceholder="userId"
              color="primary"
              {...userIdInput.bindings}
              clearable
            />
          </Grid>
        </Grid.Container>
      </div>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onClick={getLand}
      >
        Get Lands
      </Button>
    </div>
  );
};

const DeleteALand = ({ setData = () => null }) => {
  const landIdInput = useInput("");
  const { notify } = useNotifications();

  const getLand = useCallback(async () => {
    if (
      landIdInput.value === undefined ||
      landIdInput.value === null ||
      landIdInput.value === ""
    )
      return notify("Error please provide landId!", "error");

    const result = await makeFetch({
      url: `land/delete/${landIdInput.value}`,
      method: "GET",
      privateCall: false,
    });
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, [landIdInput.value]);

  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text b> Method: GET</Text>
        </div>
        <Grid.Container gap={2} style={{ marginTop: 20 }}>
          <Grid>
            <Input
              bordered
              labelPlaceholder="landId"
              color="primary"
              {...landIdInput.bindings}
              clearable
            />
          </Grid>
        </Grid.Container>
      </div>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onClick={getLand}
      >
        Delete Land
      </Button>
    </div>
  );
};

const GetGlobalLands = ({ setData = () => null }) => {
  const { notify } = useNotifications();

  const getLand = useCallback(async () => {
    const result = await makeFetch({
      url: `lands/all`,
      method: "GET",
      privateCall: false,
    });
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, []);

  return (
    <div>
      <div style={{ display: "flex", flexDirection: "column" }}>
        <Text b> Method: GET</Text>
      </div>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onClick={getLand}
      >
        Get Lands
      </Button>
    </div>
  );
};

const landUpdateEdittor = {
  landId: "A223",
  itemDetailsUpdate: {
    userId: "",
    name: "",
    description: "",
    location: "",
    rotation: "",
    landPrice: 100,
    landSize: 80,
    assets: [
      {
        assetId: "643da29f9523c40e4fa101f2",
        position: "top",
        rotation: "",
      },
    ],
  },
};

const UpdateLand = ({ setData = () => null }) => {
  const jsonInput = useInput(landUpdateEdittor);
  const { notify } = useNotifications();

  const handleChange = useCallback((text) => {
    try {
      jsonInput.setValue(text && JSON.parse(text));
    } catch (error) {}
  }, []);

  const toupdateLand = useCallback(async () => {
    const result = await makeFetch({
      url: `land/update`,
      method: "POST",
      privateCall: false,
      body: jsonInput.value,
    });
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, [jsonInput.value]);

  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text b> Method: POST</Text>
          <Text b>Editor:</Text>
        </div>
        <Grid.Container gap={2} style={{ marginTop: 20 }}>
          <AceEditor
            mode="java"
            theme="github"
            onChange={handleChange}
            name="updateLand"
            editorProps={{ $blockScrolling: true }}
            value={JSON.stringify(jsonInput.value, null, 2)}
          />
        </Grid.Container>
      </div>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onPress={toupdateLand}
      >
        Update Land
      </Button>
    </div>
  );
};

const productEditorExtra = {
  // Product_ID: null,
  Store_ID: null,
};
const productEditor = {
  Name: null,
  SKU: null,
  Category_ID: null,
  Description: null,
  Variant_Name: null,
  Variant: null,
  // Images: [
  //   {
  //     Image: null,
  //     Image_Dimensions: null,
  //     Image_Name: null,
  //     Image_Ext: null,
  //   },
  // ],
  Price: null,
  Stock: null,
  Platform: null,
  Product_URL: null,
  type: "physical or digital or both",
  max_per_order: 1,
  max_lifetime: 1,
  Model_File: "",
  Old_Price: 0,
  Physical_Price: 0,
  Old_Physical_Price: 0,
  effects: {
    xp: "+(0.6)s",
    energy: "+(15)s",
    social: "+(10)s",
    happiness: "+(8)s",
  },
};
const productEditorMain = { ...productEditorExtra, ...productEditor };
const AddProduct = ({ setData = () => null }) => {
  const jsonInput = useInput(productEditorMain);
  const { notify } = useNotifications();

  const handleChange = useCallback((text) => {
    try {
      jsonInput.setValue(text && JSON.parse(text));
    } catch (error) {}
  }, []);

  const createLand = useCallback(async () => {
    const result = await makeFetch({
      url: `products/add`,
      method: "POST",
      privateCall: false,
      body: jsonInput.value,
    });
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, [jsonInput.value]);

  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text b> Method: POST</Text>
          <Text b>Editor:</Text>
        </div>
        <Grid.Container gap={2} style={{ marginTop: 20 }}>
          <AceEditor
            mode="java"
            theme="github"
            onChange={handleChange}
            name="createLand"
            editorProps={{ $blockScrolling: true }}
            value={JSON.stringify(jsonInput.value, null, 2)}
          />
        </Grid.Container>
      </div>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onPress={createLand}
      >
        Add Product
      </Button>
    </div>
  );
};

const productModifyEditor = {
  Product_ID: null,
  Store_ID: null,
  data: productEditor,
};

const ModifyProduct = ({ setData = () => null }) => {
  const jsonInput = useInput(productModifyEditor);
  const { notify } = useNotifications();

  const handleChange = useCallback((text) => {
    try {
      jsonInput.setValue(text && JSON.parse(text));
    } catch (error) {}
  }, []);

  const createLand = useCallback(async () => {
    const result = await makeFetch({
      url: `products/modify`,
      method: "POST",
      privateCall: false,
      body: jsonInput.value,
    });
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, [jsonInput.value]);

  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text b> Method: POST</Text>
          <Text b>Editor:</Text>
        </div>
        <Grid.Container gap={2} style={{ marginTop: 20 }}>
          <AceEditor
            mode="java"
            theme="github"
            onChange={handleChange}
            name="createLand"
            editorProps={{ $blockScrolling: true }}
            value={JSON.stringify(jsonInput.value, null, 2)}
          />
        </Grid.Container>
      </div>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onPress={createLand}
      >
        Modify Product
      </Button>
    </div>
  );
};

const DeleteProduct = ({ setData = () => null }) => {
  const productIdInput = useInput("");
  const storeIdInput = useInput("");
  const { notify } = useNotifications();

  const getScene = useCallback(async () => {
    if (
      storeIdInput.value === undefined ||
      storeIdInput.value === null ||
      storeIdInput.value === ""
    )
      return notify("Error please provide store id!", "error");
    if (
      productIdInput.value === undefined ||
      productIdInput.value === null ||
      productIdInput.value === ""
    )
      return notify("Error please provide product id!", "error");
    const result = await makeFetch({
      url: `products/delete/${productIdInput.value}/${storeIdInput.value}`,
      method: "GET",
      privateCall: false,
    });
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, [productIdInput.value, storeIdInput.value]);

  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text b> Method: GET</Text>
        </div>
        <Grid.Container gap={2} style={{ marginTop: 20 }}>
          <Grid>
            <Input
              bordered
              labelPlaceholder="store_id"
              color="primary"
              {...storeIdInput.bindings}
              clearable
            />
          </Grid>
          <Grid>
            <Input
              bordered
              labelPlaceholder="product_id"
              color="primary"
              {...productIdInput.bindings}
              clearable
            />
          </Grid>
        </Grid.Container>
      </div>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onClick={getScene}
      >
        Delete Product
      </Button>
    </div>
  );
};

const GetProducts = ({ setData = () => null, isUser = false }) => {
  const { notify } = useNotifications();
  const userIdInput = useInput("");

  const getScene = useCallback(async () => {
    let url = `products/all`;
    if (isUser) {
      if (
        userIdInput.value === undefined ||
        userIdInput.value === null ||
        userIdInput.value === ""
      )
        return notify("Error please provide userId!", "error");
      url = `${url}/${userIdInput.value}`;
    }
    const result = await makeFetch({
      url,
      method: "GET",
      privateCall: false,
    });
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, [userIdInput.value]);

  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text b> Method: GET</Text>
        </div>
        {isUser && (
          <Grid.Container gap={2} style={{ marginTop: 20 }}>
            <Grid>
              <Input
                bordered
                labelPlaceholder="userId"
                color="primary"
                {...userIdInput.bindings}
                clearable
              />
            </Grid>
          </Grid.Container>
        )}
      </div>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onClick={getScene}
      >
        Get Products
      </Button>
    </div>
  );
};

const GetAProduct = ({ setData = () => null }) => {
  const { notify } = useNotifications();
  const productIdInput = useInput("");

  const getScene = useCallback(async () => {
    if (
      productIdInput.value === undefined ||
      productIdInput.value === null ||
      productIdInput.value === ""
    )
      return notify("Error please provide product id!", "error");
    const result = await makeFetch({
      url: `products/find/${productIdInput.value}`,
      method: "GET",
      privateCall: false,
    });
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, [productIdInput.value]);

  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text b> Method: GET</Text>
        </div>
        <Grid.Container gap={2} style={{ marginTop: 20 }}>
          <Grid>
            <Input
              bordered
              labelPlaceholder="product_id"
              color="primary"
              {...productIdInput.bindings}
              clearable
            />
          </Grid>
        </Grid.Container>
      </div>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onClick={getScene}
      >
        Get a Product
      </Button>
    </div>
  );
};

const BuyProduct = ({ setData = () => null }) => {
  const { notify } = useNotifications();
  const variant_idInput = useInput("");
  const couponInput = useInput("");

  const getData = useCallback(async () => {
    if (
      variant_idInput.value === undefined ||
      variant_idInput.value === null ||
      variant_idInput.value === ""
    )
      return notify("Error please provide variant id!", "error");
    const result = await makeFetch({
      url: `products/buy/${variant_idInput.value}?coupon=${couponInput.value}`,
      method: "GET",
      privateCall: false,
    });
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, [variant_idInput.value, couponInput.value]);

  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text b> Method: GET</Text>
        </div>
        <Grid.Container gap={2} style={{ marginTop: 20 }}>
          <Grid>
            <Input
              bordered
              labelPlaceholder="variant_id"
              color="primary"
              {...variant_idInput.bindings}
              clearable
            />
          </Grid>
          <Grid>
            <Input
              bordered
              labelPlaceholder="coupon"
              color="primary"
              {...couponInput.bindings}
              clearable
            />
          </Grid>
        </Grid.Container>
      </div>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onClick={getData}
      >
        Buy Product
      </Button>
    </div>
  );
};

const UploadImageProduct = ({ setData = () => null }) => {
  const { notify } = useNotifications();
  const [file, setFile] = useState(null);
  const variant_idInput = useInput("");

  const handleFileChange = (event) => {
    setFile(event.target.files[0]);
  };

  const getStats = useCallback(async () => {
    if (
      variant_idInput.value === undefined ||
      variant_idInput.value === null ||
      variant_idInput.value === ""
    )
      return notify("Error please provide variant id!", "error");

    if (!file) {
      return notify("Error please select a file!", "error");
    }

    const validImageTypes = ["image/gif", "image/jpeg", "image/png"];
    if (!validImageTypes.includes(file.type)) {
      return notify("Error please select a valid image file!", "error");
    }

    const formData = new FormData();
    formData.append("file", file);

    const authorizationToken = JSON.parse(
      localStorage.getItem("authorizationToken")
    );

    const result = await fetch(
      `/api/v1/products/upload/image/${variant_idInput.value}`,
      {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${authorizationToken}`,
        },
      }
    )
      .then((response) => {
        if (!response.ok) {
          return null; // Return null if the response is not ok
        }
        return response.json();
      })
      .catch((error) => {
        console.error("Error:", error);
        return null; // Return null in case of an error
      });

    if (result === undefined || result === null) {
      notify("Error uploading failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, [file, variant_idInput.value]);

  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <Text b> Method: POST</Text>
      </div>
      <Grid.Container gap={2} style={{ marginTop: 20 }}>
        <Grid>
          <Input
            bordered
            labelPlaceholder="variant_id"
            color="primary"
            {...variant_idInput.bindings}
            clearable
          />
        </Grid>
        <Grid>
          <input type="file" onChange={handleFileChange} />
        </Grid>
      </Grid.Container>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onClick={getStats}
      >
        Upload
      </Button>
    </div>
  );
};

const CreateStore = ({ setData = () => null }) => {
  const jsonInput = useInput({
    storeName: "Store 1",
  });
  const { notify } = useNotifications();

  const handleChange = useCallback((text) => {
    try {
      jsonInput.setValue(text && JSON.parse(text));
    } catch (error) {}
  }, []);

  const createStore = useCallback(async () => {
    const result = await makeFetch({
      url: `store/create`,
      method: "POST",
      privateCall: false,
      body: jsonInput.value,
    });
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, [jsonInput.value]);
  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text b> Method: POST</Text>
          <Text b>Editor:</Text>
        </div>
        <Grid.Container gap={2} style={{ marginTop: 20 }}>
          <AceEditor
            mode="java"
            theme="github"
            onChange={handleChange}
            name="createStore"
            editorProps={{ $blockScrolling: true }}
            value={JSON.stringify(jsonInput.value, null, 2)}
          />
        </Grid.Container>
      </div>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onPress={createStore}
      >
        Create Store
      </Button>
    </div>
  );
};

const GetStoreProducts = ({ setData = () => null }) => {
  const { notify } = useNotifications();
  const storeIdInput = useInput("");

  const getScene = useCallback(async () => {
    if (
      storeIdInput.value === undefined ||
      storeIdInput.value === null ||
      storeIdInput.value === ""
    )
      return notify("Error please provide store id!", "error");
    const result = await makeFetch({
      url: `store/products/${storeIdInput.value}`,
      method: "GET",
      privateCall: false,
    });
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, [storeIdInput.value]);

  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text b> Method: GET</Text>
        </div>
        <Grid.Container gap={2} style={{ marginTop: 20 }}>
          <Grid>
            <Input
              bordered
              labelPlaceholder="store_id"
              color="primary"
              {...storeIdInput.bindings}
              clearable
            />
          </Grid>
        </Grid.Container>
      </div>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onClick={getScene}
      >
        Get Store
      </Button>
    </div>
  );
};
/// LeaderBoard

const GetLeaderBoards = ({ setData = () => null }) => {
  const jsonInput = useInput({
    eventId: "",
    count: 3,
    userId: "",
    leaderboard_Type: "",
    valueType: "",
  });

  const { notify } = useNotifications();

  const handleChange = useCallback((text) => {
    try {
      jsonInput.setValue(text && JSON.parse(text));
    } catch (error) {}
  }, []);

  const getData = useCallback(async () => {
    if (
      jsonInput.value.eventId === undefined ||
      jsonInput.value.eventId === null ||
      jsonInput.value.eventId === ""
    )
      return notify("Error please provide event id!", "error");
    if (
      jsonInput.value.count === undefined ||
      jsonInput.value.count === null ||
      jsonInput.value.count === "" ||
      Number(jsonInput.value.count) <= 0
    )
      return notify("Error count must be more than 0!", "error");
    const result = await makeFetch({
      url: `leaderboard/find`,
      method: "POST",
      privateCall: false,
      body: jsonInput.value,
    });
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, [jsonInput.value]);

  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text> Method: POST</Text>
        </div>
        <Grid.Container gap={2} style={{ marginTop: 20 }}>
          <AceEditor
            mode="java"
            theme="github"
            onChange={handleChange}
            name="getleaderboard"
            editorProps={{ $blockScrolling: true }}
            value={JSON.stringify(jsonInput.value, null, 2)}
          />
        </Grid.Container>
      </div>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onClick={getData}
      >
        Get LeaderBoard
      </Button>
    </div>
  );
};

const AddToLeaderBoards = ({ setData = () => null }) => {
  const jsonInput = useInput({
    eventId: "",
    userId: "",
    value: 70.53,
    valueType: "",
    leaderBoardCount: 9,
  });
  const { notify } = useNotifications();

  const handleChange = useCallback((text) => {
    try {
      jsonInput.setValue(text && JSON.parse(text));
    } catch (error) {}
  }, []);

  const addToLeaderBoard = useCallback(async () => {
    const result = await makeFetch({
      url: `leaderboard/add`,
      method: "POST",
      privateCall: false,
      body: jsonInput.value,
    });
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, [jsonInput.value]);
  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text b> Method: POST</Text>
          <Text b>Editor:</Text>
        </div>
        <Grid.Container gap={2} style={{ marginTop: 20 }}>
          <AceEditor
            mode="java"
            theme="github"
            onChange={handleChange}
            name="addToLeaderBoard"
            editorProps={{ $blockScrolling: true }}
            value={JSON.stringify(jsonInput.value, null, 2)}
          />
        </Grid.Container>
      </div>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onPress={addToLeaderBoard}
      >
        Add User
      </Button>
    </div>
  );
};

const GetTempLeaderBoards = ({ setData = () => null }) => {
  const eventId = useInput("");
  const { notify } = useNotifications();

  const getData = useCallback(async () => {
    if (
      eventId.value === undefined ||
      eventId.value === null ||
      eventId.value === ""
    )
      return notify("Error please provide event id!", "error");
    const result = await makeFetch({
      url: `leaderboard/temp/${eventId.value || null}`,
      method: "GET",
      privateCall: false,
    });
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, [eventId.value]);

  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text b> Method: GET</Text>
        </div>
        <Grid.Container gap={2} style={{ marginTop: 20 }}>
          <Grid>
            <Input
              bordered
              labelPlaceholder="eventId"
              color="primary"
              {...eventId.bindings}
              clearable
            />
          </Grid>
        </Grid.Container>
      </div>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onClick={getData}
      >
        Get Temp LeaderBoard
      </Button>
    </div>
  );
};

const AddToTempLeaderBoards = ({ setData = () => null }) => {
  const jsonInput = useInput({
    eventId: "",
    userId: "",
    results: {},
  });
  const { notify } = useNotifications();

  const handleChange = useCallback((text) => {
    try {
      jsonInput.setValue(text && JSON.parse(text));
    } catch (error) {}
  }, []);

  const addToLeaderBoard = useCallback(async () => {
    const result = await makeFetch({
      url: `leaderboard/temp/add`,
      method: "POST",
      privateCall: false,
      body: jsonInput.value,
    });
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, [jsonInput.value]);
  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text b> Method: POST</Text>
          <Text b>Editor:</Text>
        </div>
        <Grid.Container gap={2} style={{ marginTop: 20 }}>
          <AceEditor
            mode="java"
            theme="github"
            onChange={handleChange}
            name="addToTempLeaderBoard"
            editorProps={{ $blockScrolling: true }}
            value={JSON.stringify(jsonInput.value, null, 2)}
          />
        </Grid.Container>
      </div>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onPress={addToLeaderBoard}
      >
        Add Temp User
      </Button>
    </div>
  );
};

const AddToServer = ({ setData = () => null }) => {
  const serverAddress = useInput("");
  const serverPort = useInput("");
  const maxPlayers = useInput(1);
  const typeOfServer = useInput("Youniverse");
  const serverName = useInput("");

  const { notify } = useNotifications();

  const addServer = useCallback(async () => {
    if (
      serverAddress.value === undefined ||
      serverAddress.value === null ||
      serverAddress.value === ""
    )
      return notify("Error please provide server address!", "error");
    if (
      serverPort.value === undefined ||
      serverPort.value === null ||
      serverPort.value === ""
    )
      return notify("Error please provide server port!", "error");
    if (
      typeOfServer.value === undefined ||
      typeOfServer.value === null ||
      typeOfServer.value === ""
    )
      return notify("Error please provide server type!", "error");

    if (
      serverName.value === undefined ||
      serverName.value === null ||
      serverName.value === ""
    )
      return notify("Error please provide server name!", "error");
    const result = await makeFetch({
      url: `server/add/${serverAddress.value}/${serverPort.value}/${maxPlayers.value}/${typeOfServer.value}/${serverName.value}`,
      method: "GET",
      privateCall: false,
    });
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, [
    serverAddress.value,
    serverPort.value,
    maxPlayers.value,
    typeOfServer.value,
    serverName.value,
  ]);

  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text b> Method: GET</Text>
        </div>
        <Grid.Container gap={2} style={{ marginTop: 20 }}>
          <Grid>
            <Input
              bordered
              labelPlaceholder="serverAddress"
              color="primary"
              {...serverAddress.bindings}
              clearable
            />
          </Grid>
          <Grid>
            <Input
              bordered
              labelPlaceholder="serverPort"
              color="primary"
              {...serverPort.bindings}
              clearable
            />
          </Grid>
          <Grid>
            <Input
              bordered
              labelPlaceholder="maxPlayers"
              color="primary"
              {...maxPlayers.bindings}
              clearable
              type="number"
            />
          </Grid>
          <Grid>
            <Input
              bordered
              labelPlaceholder="typeOfServer"
              color="primary"
              {...typeOfServer.bindings}
              clearable
            />
          </Grid>
          <Grid>
            <Input
              bordered
              labelPlaceholder="serverName"
              color="primary"
              {...serverName.bindings}
              clearable
            />
          </Grid>
        </Grid.Container>
      </div>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onPress={addServer}
      >
        Add Server
      </Button>
    </div>
  );
};

const JoinServer = ({ setData = () => null }) => {
  const serverAddress = useInput("");
  const serverPort = useInput("");

  const { notify } = useNotifications();

  const addServer = useCallback(async () => {
    if (
      serverAddress.value === undefined ||
      serverAddress.value === null ||
      serverAddress.value === ""
    )
      return notify("Error please provide server address!", "error");
    if (
      serverAddress.value === undefined ||
      serverAddress.value === null ||
      serverAddress.value === ""
    )
      return notify("Error please provide server address!", "error");
    if (
      serverPort.value === undefined ||
      serverPort.value === null ||
      serverPort.value === ""
    )
      return notify("Error please provide server port!", "error");
    const result = await makeFetch({
      url: `server/join/${serverAddress.value}/${serverPort.value}`,
      method: "GET",
      privateCall: false,
    });
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, [serverAddress.value, serverPort.value]);

  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text b> Method: GET</Text>
        </div>
        <Grid.Container gap={2} style={{ marginTop: 20 }}>
          <Grid>
            <Input
              bordered
              labelPlaceholder="serverAddress"
              color="primary"
              {...serverAddress.bindings}
              clearable
            />
          </Grid>
          <Grid>
            <Input
              bordered
              labelPlaceholder="serverPort"
              color="primary"
              {...serverPort.bindings}
              clearable
              type="number"
            />
          </Grid>
        </Grid.Container>
      </div>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onPress={addServer}
      >
        Join Server
      </Button>
    </div>
  );
};

const LeaveServer = ({ setData = () => null }) => {
  const serverAddress = useInput("");
  const serverPort = useInput("");

  const { notify } = useNotifications();

  const addServer = useCallback(async () => {
    if (
      serverAddress.value === undefined ||
      serverAddress.value === null ||
      serverAddress.value === ""
    )
      return notify("Error please provide server address!", "error");
    if (
      serverAddress.value === undefined ||
      serverAddress.value === null ||
      serverAddress.value === ""
    )
      return notify("Error please provide server address!", "error");
    if (
      serverPort.value === undefined ||
      serverPort.value === null ||
      serverPort.value === ""
    )
      return notify("Error please provide server port!", "error");
    const result = await makeFetch({
      url: `server/leave/${serverAddress.value}/${serverPort.value}`,
      method: "GET",
      privateCall: false,
    });
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, [serverAddress.value, serverPort.value]);

  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text b> Method: GET</Text>
        </div>
        <Grid.Container gap={2} style={{ marginTop: 20 }}>
          <Grid>
            <Input
              bordered
              labelPlaceholder="serverAddress"
              color="primary"
              {...serverAddress.bindings}
              clearable
            />
          </Grid>
          <Grid>
            <Input
              bordered
              labelPlaceholder="serverPort"
              color="primary"
              {...serverPort.bindings}
              clearable
              type="number"
            />
          </Grid>
        </Grid.Container>
      </div>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onPress={addServer}
      >
        Leave Server
      </Button>
    </div>
  );
};

const FindServerByServerName = ({ setData = () => null }) => {
  const serverName = useInput("");
  const { notify } = useNotifications();

  const getData = useCallback(async () => {
    if (
      serverName.value === undefined ||
      serverName.value === null ||
      serverName.value === ""
    )
      return notify("Error please provide server name!", "error");
    const result = await makeFetch({
      url: `server/find/${serverName.value}`,
      method: "GET",
      privateCall: false,
    });
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, [serverName.value]);

  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text b> Method: GET</Text>
        </div>
        <Grid.Container gap={2} style={{ marginTop: 20 }}>
          <Grid>
            <Input
              bordered
              labelPlaceholder="serverName"
              color="primary"
              {...serverName.bindings}
              clearable
            />
          </Grid>
        </Grid.Container>
      </div>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onPress={getData}
      >
        Find Server
      </Button>
    </div>
  );
};
const FindServersByServerType = ({ setData = () => null }) => {
  const typeOfServer = useInput("");
  const { notify } = useNotifications();

  const getData = useCallback(async () => {
    if (
      typeOfServer.value === undefined ||
      typeOfServer.value === null ||
      typeOfServer.value === ""
    )
      return notify("Error please provide server type!", "error");
    const result = await makeFetch({
      url: `server/find/type/${typeOfServer.value}`,
      method: "GET",
      privateCall: false,
    });
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, [typeOfServer.value]);

  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text b> Method: GET</Text>
        </div>
        <Grid.Container gap={2} style={{ marginTop: 20 }}>
          <Grid>
            <Input
              bordered
              labelPlaceholder="typeOfServer"
              color="primary"
              {...typeOfServer.bindings}
              clearable
            />
          </Grid>
        </Grid.Container>
      </div>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onPress={getData}
      >
        Find Servers
      </Button>
    </div>
  );
};
const PingServer = ({ setData = () => null }) => {
  const serverAddress = useInput("");
  const serverPort = useInput("");

  const { notify } = useNotifications();

  const actionFunc = useCallback(async () => {
    if (
      serverAddress.value === undefined ||
      serverAddress.value === null ||
      serverAddress.value === ""
    )
      return notify("Error please provide server address!", "error");
    if (
      serverPort.value === undefined ||
      serverPort.value === null ||
      serverPort.value === ""
    )
      return notify("Error please provide server port!", "error");
    const result = await makeFetch({
      url: `server/ping/${serverAddress.value}/${serverPort.value}`,
      method: "GET",
      privateCall: false,
    });
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, [serverAddress.value, serverPort.value]);

  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text b> Method: GET</Text>
        </div>
        <Grid.Container gap={2} style={{ marginTop: 20 }}>
          <Grid>
            <Input
              bordered
              labelPlaceholder="serverAddress"
              color="primary"
              {...serverAddress.bindings}
              clearable
            />
          </Grid>
          <Grid>
            <Input
              bordered
              labelPlaceholder="serverPort"
              color="primary"
              {...serverPort.bindings}
              clearable
              type="number"
            />
          </Grid>
        </Grid.Container>
      </div>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onPress={actionFunc}
      >
        Ping Server
      </Button>
    </div>
  );
};

const LockServer = ({ setData = () => null, locked = false }) => {
  const serverAddress = useInput("");
  const serverPort = useInput("");

  const { notify } = useNotifications();

  const actionFunc = useCallback(async () => {
    if (
      serverAddress.value === undefined ||
      serverAddress.value === null ||
      serverAddress.value === ""
    )
      return notify("Error please provide server address!", "error");
    if (
      serverPort.value === undefined ||
      serverPort.value === null ||
      serverPort.value === ""
    )
      return notify("Error please provide server port!", "error");
    const result = await makeFetch({
      url: `server/lock/${serverAddress.value}/${serverPort.value}/${locked}`,
      method: "GET",
      privateCall: false,
    });
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, [serverAddress.value, serverPort.value]);

  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text b> Method: GET</Text>
        </div>
        <Grid.Container gap={2} style={{ marginTop: 20 }}>
          <Grid>
            <Input
              bordered
              labelPlaceholder="serverAddress"
              color="primary"
              {...serverAddress.bindings}
              clearable
            />
          </Grid>
          <Grid>
            <Input
              bordered
              labelPlaceholder="serverPort"
              color="primary"
              {...serverPort.bindings}
              clearable
              type="number"
            />
          </Grid>
        </Grid.Container>
      </div>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onPress={actionFunc}
      >
        {`${locked === true ? "Lock" : "UnLock"}`} Server
      </Button>
    </div>
  );
};

const RemoveServer = ({ setData = () => null }) => {
  const serverAddress = useInput("");
  const serverPort = useInput("");

  const { notify } = useNotifications();

  const actionFunc = useCallback(async () => {
    if (
      serverAddress.value === undefined ||
      serverAddress.value === null ||
      serverAddress.value === ""
    )
      return notify("Error please provide server address!", "error");
    if (
      serverPort.value === undefined ||
      serverPort.value === null ||
      serverPort.value === ""
    )
      return notify("Error please provide server port!", "error");
    const result = await makeFetch({
      url: `server/remove/${serverAddress.value}/${serverPort.value}`,
      method: "GET",
      privateCall: false,
    });
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, [serverAddress.value, serverPort.value]);

  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text b> Method: GET</Text>
        </div>
        <Grid.Container gap={2} style={{ marginTop: 20 }}>
          <Grid>
            <Input
              bordered
              labelPlaceholder="serverAddress"
              color="primary"
              {...serverAddress.bindings}
              clearable
            />
          </Grid>
          <Grid>
            <Input
              bordered
              labelPlaceholder="serverPort"
              color="primary"
              {...serverPort.bindings}
              clearable
              type="number"
            />
          </Grid>
        </Grid.Container>
      </div>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onPress={actionFunc}
      >
        Remove Server
      </Button>
    </div>
  );
};

const GetAllServers = ({ setData = () => null }) => {
  const { notify } = useNotifications();

  const actionFunc = useCallback(async () => {
    const result = await makeFetch({
      url: `server/all`,
      method: "GET",
      privateCall: false,
    });
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, []);

  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text> Method: GET</Text>
        </div>
      </div>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onPress={actionFunc}
      >
        All Servers
      </Button>
    </div>
  );
};

const TransactionAction = ({ setData = () => null }) => {
  const actionType = useInput("");
  const serverAddress = useInput("");
  const jsonInput = useInput({
    actionsDevTransaction_key: "",
    amount: 0,
    reason: "can be anything or empty",
    metaData: {},
  });
  const { notify } = useNotifications();

  const handleChange = useCallback((text) => {
    try {
      jsonInput.setValue(text && JSON.parse(text));
    } catch (error) {}
  }, []);

  const makeAction = useCallback(async () => {
    if (
      actionType.value === undefined ||
      actionType.value === null ||
      actionType.value === ""
    )
      return notify("Error please provide action!", "error");

    const result = await makeFetch({
      url: `transaction/${actionType.value}`,
      method: "POST",
      privateCall: false,
      body: jsonInput.value,
    });
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, [actionType.value, jsonInput.value]);

  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text b> Method: POST</Text>
        </div>
        <Grid.Container gap={2} style={{ marginTop: 20 }} direction="column">
          <Grid>
            <Input
              bordered
              labelPlaceholder="action"
              color="primary"
              {...actionType.bindings}
              clearable
            />
          </Grid>
          <AceEditor
            mode="java"
            theme="github"
            onChange={handleChange}
            name="coinActionData"
            editorProps={{ $blockScrolling: true }}
            value={JSON.stringify(jsonInput.value, null, 2)}
            height="200px"
          />
        </Grid.Container>
      </div>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onPress={makeAction}
      >
        Make Action
      </Button>
    </div>
  );
};

const TransactionActionName = ({ setData = () => null }) => {
  const transaction_name = useInput("");
  const { notify } = useNotifications();

  const makeAction = useCallback(async () => {
    if (
      transaction_name.value === undefined ||
      transaction_name.value === null ||
      transaction_name.value === ""
    )
      return notify("Error please provide transaction name!", "error");

    const result = await makeFetch({
      url: `transaction/target/${transaction_name.value}`,
      method: "GET",
      privateCall: false,
    });
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, [transaction_name.value]);

  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text b> Method: GET</Text>
        </div>
        <Grid.Container gap={2} style={{ marginTop: 20 }} direction="column">
          <Grid>
            <Input
              bordered
              labelPlaceholder="transaction_name"
              color="primary"
              {...transaction_name.bindings}
              clearable
            />
          </Grid>
        </Grid.Container>
      </div>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onPress={makeAction}
      >
        Make Action
      </Button>
    </div>
  );
};
const GetUsersTransactions = ({ setData = () => null }) => {
  const { notify } = useNotifications();

  const actionFunc = useCallback(async () => {
    const result = await makeFetch({
      url: `transaction/user`,
      method: "GET",
      privateCall: false,
    });
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, []);

  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text> Method: GET</Text>
        </div>
      </div>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onPress={actionFunc}
      >
        Transactions
      </Button>
    </div>
  );
};

const GetUsersWalletAmount = ({ setData = () => null }) => {
  const { notify } = useNotifications();

  const actionFunc = useCallback(async () => {
    const result = await makeFetch({
      url: `transaction/user/amount`,
      method: "GET",
      privateCall: false,
    });
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, []);

  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text> Method: GET</Text>
        </div>
      </div>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onPress={actionFunc}
      >
        Amount
      </Button>
    </div>
  );
};

// AI
const AiImagePrompt = ({ setData = () => null }) => {
  const [gettingData, setGettingData] = useState(false);
  const jsonInput = useInput({
    prompt: "batman",
    imageWidth: 400,
    imageHeight: 400,
    imagePosition: { x: 185, y: 550 },
    imagePositionBack: { x: 700, y: 550 },
    frontOrBackOrBoth: "front", // front, back, both
    fileName: "batman",
  });
  const { notify } = useNotifications();

  const handleChange = useCallback((text) => {
    try {
      jsonInput.setValue(text && JSON.parse(text));
    } catch (error) {}
  }, []);

  const makeAction = useCallback(async () => {
    if (gettingData === true) return;
    setGettingData(true);
    const result = await makeFetch({
      url: `ai/generate/image`,
      method: "POST",
      privateCall: false,
      body: jsonInput.value,
    });
    setGettingData(false);
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, [jsonInput.value, gettingData]);

  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text b> Method: POST</Text>
        </div>
        <Grid.Container gap={2} style={{ marginTop: 20 }} direction="column">
          <AceEditor
            mode="java"
            theme="github"
            onChange={handleChange}
            name="aiImagePromt"
            editorProps={{ $blockScrolling: true }}
            value={JSON.stringify(jsonInput.value, null, 2)}
            height="300px"
          />
        </Grid.Container>
      </div>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onPress={makeAction}
      >
        {gettingData ? "Generating..." : "Generate Image"}
      </Button>
    </div>
  );
};

// billboard
const GetAllBillBoard = ({ setData = () => null }) => {
  const { notify } = useNotifications();

  const actionFunc = useCallback(async () => {
    const result = await makeFetch({
      url: `billboard/find/all`,
      method: "GET",
      privateCall: false,
    });
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, []);

  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text> Method: GET</Text>
        </div>
      </div>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onPress={actionFunc}
      >
        Get All BillBoard
      </Button>
    </div>
  );
};

const GetABillBoardByName = ({ setData = () => null }) => {
  const billboard_name = useInput("");
  const { notify } = useNotifications();

  const actionFunc = useCallback(async () => {
    if (
      billboard_name.value === undefined ||
      billboard_name.value === null ||
      billboard_name.value === ""
    )
      return notify("Error please provide billboard name!", "error");
    const result = await makeFetch({
      url: `billboard/find/name/${billboard_name.value}`,
      method: "GET",
      privateCall: false,
    });
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, [billboard_name.value]);

  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text> Method: GET</Text>
        </div>
        <Grid.Container gap={2} style={{ marginTop: 20 }} direction="column">
          <Grid>
            <Input
              bordered
              labelPlaceholder="billboard_name"
              color="primary"
              {...billboard_name.bindings}
              clearable
            />
          </Grid>
        </Grid.Container>
      </div>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onPress={actionFunc}
      >
        Get All BillBoard
      </Button>
    </div>
  );
};

// mode
const GetAllZippedModels = ({ setData = () => null }) => {
  const forceGet = useInput("false");
  const { notify } = useNotifications();

  const actionFunc = useCallback(async () => {
    const result = await makeFetch({
      url: `model/find/zip/all/${forceGet?.value || false}`,
      method: "GET",
      privateCall: false,
    });
    if (result === undefined || result === null) {
      notify("Error getting data failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setData(result);
    }
  }, [forceGet.value]);

  return (
    <div>
      <div style={{ marginTop: 30, marginLeft: 10 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text> Method: GET</Text>
        </div>
        <Grid.Container gap={2} style={{ marginTop: 20 }} direction="column">
          <Grid>
            <Input
              bordered
              labelPlaceholder="force get data"
              color="primary"
              {...forceGet.bindings}
              clearable
              type="boolean"
              defaultValue={false}
            />
          </Grid>
        </Grid.Container>
      </div>
      <Button
        icon={<UserIcon fill="currentColor" />}
        color="success"
        flat
        style={{ marginTop: 20 }}
        onPress={actionFunc}
      >
        Get Models
      </Button>
    </div>
  );
};
const PlayGround = () => {
  const [makeLogInfoVisible, setMakeLogInfoVisible] = useState(false);
  const [dataResults, setDataResults] = useState(null);
  const [developerKey = "", setDeveloperKey] = useStorage("developerKey");
  const [userId = "", setUserId] = useStorage("userId");
  const [v_api = "", setV_api] = useStorage("v_api");

  const setData = useCallback((e) => {
    setDataResults(e);
    setMakeLogInfoVisible(true);
  }, []);

  return (
    <ResultsInfo
      data={dataResults}
      makeVisible={makeLogInfoVisible}
      setVisible={(e) => {
        setDataResults(null);
        setMakeLogInfoVisible(e);
      }}
    >
      <div style={{ paddingBottom: 200 }}>
        <div style={{ flexDirection: "column", display: "flex" }}>
          <Text>API: https://api.youniverse.game/api/{v_api}</Text>
          <Text>Dev Key: {developerKey}</Text>
          <Text>User Id: {userId}</Text>
        </div>
        <Collapse.Group>
          <Collapse title="Accounts" subtitle="All apis regarding account.">
            <Text>Here you will see list of Account methods</Text>
            <Collapse.Group>
              <Collapse
                title="Create Account"
                subtitle={
                  <>
                    Can be used to create and get existing account as long the
                    required values are passed.{" "}
                    <Text>{`{{api}}/account/create`}</Text>
                  </>
                }
              >
                <Text>
                  idType: ["wallet", "facebook", "google", "apple", "email",
                  "guest", "developer"]
                </Text>
                <CreateAccount setData={setData} />
              </Collapse>
              <Collapse
                title="Get Authorization Token"
                subtitle={
                  <>
                    Can be used to get any account authorizationToken.{" "}
                    <Text>{`{{api}}/account/token`}</Text>
                  </>
                }
              >
                <Text>
                  developerAccessKey can be obtained from /account/find/details
                  by passing accessKey as keyNames and accessKey should be
                  provided.{" "}
                </Text>
                <GetAuthorizationToken setData={setData} />
              </Collapse>
              <Collapse
                title="Find Account + Details"
                subtitle={
                  <>
                    Can be used to get existing account and its needed data as
                    long the required values are passed.{" "}
                    <Text>{`{{api}}/account/find/details`}</Text>
                  </>
                }
              >
                <Text>can use userId or email to find</Text>
                <Text>
                  idType: ["wallet", "facebook", "google", "apple", "email",
                  "guest", "developer"]
                </Text>
                <GetAccountAndDetails setData={setData} isDeveloper={false} />
              </Collapse>
              <Collapse
                title="Find Account + Details Without Auth Token"
                subtitle={
                  <>
                    Can be used to get existing account and its needed data as
                    long the required values are passed.{" "}
                    <Text>{`{{api}}/account/dev/find/details`}</Text>
                  </>
                }
              >
                <Text>can use userId or email to find</Text>
                <Text>
                  idType: ["wallet", "facebook", "google", "apple", "email",
                  "guest", "developer"]
                </Text>
                <GetAccountAndDetails setData={setData} isDeveloper={true} />
              </Collapse>
              <Collapse
                title="Find Account By Type And Id"
                subtitle={
                  <>
                    Can be used to get existing account and its needed data as
                    long the required values are passed.{" "}
                    <Text>{`{{api}}/account/find/:idType/:id`}</Text>
                  </>
                }
              >
                <Text>
                  This does not require users userId, it will just be used to
                  find user.
                </Text>
                <Text>
                  This will not return account details, its more of to use to
                  quickly check if user exists.
                </Text>
                <Text>
                  idType: [ "wallet", "facebook", "google", "apple", "email",
                  "guest", "developer"]
                </Text>
                <Text>
                  id: this is the id given to the idType for instance if its
                  "google" then it will be the id given by google, in the case
                  of "email" or "developer" the id is the password or
                  developerKey
                </Text>
                <FindAccountById setData={setData} />
              </Collapse>
              <Collapse
                title="Update Account"
                subtitle={
                  <>
                    Can be used to update existing account.{" "}
                    <Text>{`{{api}}/account/update`}</Text>
                  </>
                }
              >
                <Text>
                  idType: ["wallet", "facebook", "google", 'apple", email",
                  "guest", "developer"]
                </Text>
                <Text>itemDetailsAdd is meant for array keys</Text>
                <Text>
                  if itemDetailsAdd and itemDetailsUpdate have same array key
                  they will merge if the key value is an array
                </Text>
                <Text>Example:</Text>
                <ReactJson
                  src={{
                    userId: "642e4b2912f03fed9da90285",
                    itemDetailsAdd: {
                      friends: ["6424ea8fc68544a497de2942"],
                    },
                    itemDetailsUpdate: {
                      handle: "user1",
                    },
                  }}
                />
                <UpdateAccount setData={setData} />
              </Collapse>
              <Collapse
                title="Update Account Without Auth Token"
                subtitle={
                  <>
                    Can be used to update existing account by developer.{" "}
                    <Text>{`{{api}}/account/dev/update`}</Text>
                  </>
                }
              >
                <Text>
                  idType: ["wallet", "facebook", "google", 'apple", email",
                  "guest", "developer"]
                </Text>
                <Text>itemDetailsAdd is meant for array keys</Text>
                <Text>
                  if itemDetailsAdd and itemDetailsUpdate have same array key
                  they will merge if the key value is an array
                </Text>
                <Text>Example:</Text>
                <ReactJson
                  src={{
                    userId: "642e4b2912f03fed9da90285",
                    itemDetailsAdd: {
                      friends: ["6424ea8fc68544a497de2942"],
                    },
                    itemDetailsUpdate: {
                      handle: "user1",
                    },
                  }}
                />
                <UpdateAccount setData={setData} isDeveloper={true} />
              </Collapse>
              <Collapse
                title="Referrals"
                subtitle={
                  <>
                    Can be used to users referrals.{" "}
                    <Text>{`{{api}}/account/referrals/:referralsId`}</Text>
                  </>
                }
              >
                <GetUsersReferrals setData={setData} isDeveloper={true} />
              </Collapse>
              <Collapse
                title="Existing Handles"
                subtitle={
                  <>
                    Can be used to check if handles are taken or not.{" "}
                    <Text>{`{{api}}/account/handles/exist`}</Text>
                  </>
                }
              >
                <Text>Example:</Text>
                <ReactJson
                  src={{
                    handles: ["james"],
                  }}
                />
                <GetExistingHandles setData={setData} />
              </Collapse>
              <Collapse
                title="Update Stats"
                subtitle={
                  <>
                    Can be used to account stats.{" "}
                    <Text>{`{{api}}/account/stats/update`}</Text>
                  </>
                }
              >
                <UpdateStats setData={setData} />
              </Collapse>
              <Collapse
                title="Get Stats"
                subtitle={
                  <>
                    Can be used to get stats.{" "}
                    <Text>{`{{api}}/account/stats/:userId`}</Text>
                  </>
                }
              >
                <GetStats setData={setData} />
              </Collapse>
            </Collapse.Group>
          </Collapse>
        </Collapse.Group>
        <Collapse.Group>
          <Collapse title="Scenes" subtitle="All apis regarding scenes.">
            <Text>Here you will see list of Scenes methods</Text>
            <Collapse.Group>
              <Collapse
                title="Create Scene"
                subtitle={
                  <>
                    Can be used to create a scene.{" "}
                    <Text>{`{{api}}/scenes/create`}</Text>
                  </>
                }
              >
                <Text>Example:</Text>
                <ReactJson
                  src={{
                    userId: "641a861ccc0c49e524ab5d43",
                    name: "just a name 3",
                    description: " just a description 3",
                  }}
                />
                <CreateScene setData={setData} />
              </Collapse>
              <Collapse
                title="Get A Users Scene"
                subtitle={
                  <>
                    Can be used to get a scene belonging to a user.{" "}
                    <Text>{`{{api}}/scenes/find/:userId/:sceneId`}</Text>
                  </>
                }
              >
                <GetUsersScene setData={setData} />
              </Collapse>
              <Collapse
                title="Get All Users Scenes"
                subtitle={
                  <>
                    Can be used to get existing account scenes.{" "}
                    <Text>{`{{api}}/scenes/all/:userId`}</Text>
                  </>
                }
              >
                <GetAllUsersScene setData={setData} />
              </Collapse>
              <Collapse
                title="Delete Scene"
                subtitle={
                  <>
                    Can be used to delete a scene.{" "}
                    <Text>{`{{api}}/scene/delete/:sceneId`}</Text>
                  </>
                }
              >
                <DeleteScene setData={setData} />
              </Collapse>
              <Collapse
                title="Update Scene"
                subtitle={
                  <>
                    Can be used to update existing scene.{" "}
                    <Text>{`{{api}}/scene/update`}</Text>
                  </>
                }
              >
                <Text>itemDetailsAdd is meant for array keys</Text>
                <Text>
                  if itemDetailsAdd and itemDetailsUpdate have same array key
                  they will merge if the key value is an array
                </Text>
                <Text>Example:</Text>
                <ReactJson src={sceneUpdateEdittor} />
                <UpdateScene setData={setData} />
              </Collapse>
            </Collapse.Group>
          </Collapse>
        </Collapse.Group>
        <Collapse.Group>
          <Collapse title="Assets" subtitle="All apis regarding assets.">
            <Text>Here you will see list of Assets methods</Text>
            <Collapse.Group>
              <Collapse
                title="Create Asset"
                subtitle={
                  <>
                    Can be used to create an asset.{" "}
                    <Text>{`{{api}}/assets/create`}</Text>
                  </>
                }
              >
                <Text>key: the key of the asset, must be unique</Text>
                <Text>url: assets url</Text>
                <Text>
                  owners: array of users id, optional when dev creating asset
                </Text>
                <Text>Example:</Text>
                <ReactJson src={assetEdditor} />
                <CreateAsset setData={setData} />
              </Collapse>
              <Collapse
                title="Buy Asset"
                subtitle={
                  <>
                    Can be used to buy an asset.{" "}
                    <Text>{`{{api}}/asset/buy`}</Text>
                  </>
                }
              >
                <Text>Example:</Text>
                <ReactJson src={assetBuyEdditor} />
                <BuyAsset setData={setData} />
              </Collapse>
              <Collapse
                title="Get An Asset"
                subtitle={
                  <>
                    Can be used to get an asset belonging to a user.{" "}
                    <Text>{`{{api}}/assets/find/:userId/:assetId`}</Text>
                  </>
                }
              >
                <GetAsset setData={setData} checkOwner={true} />
              </Collapse>
              <Collapse
                title="Get All Users Assets"
                subtitle={
                  <>
                    Can be used to get existing account assets.{" "}
                    <Text>{`{{api}}/assets/all/:userId`}</Text>
                  </>
                }
              >
                <GetAllUsersAssets setData={setData} checkOwner={true} />
              </Collapse>
              <Collapse
                title="Get An Asset & Bypass Owners"
                subtitle={
                  <>
                    Can be used to get an asset belonging to anyone.{" "}
                    <Text>{`{{api}}/assets/find/:assetId`}</Text>
                  </>
                }
              >
                <GetAsset setData={setData} checkOwner={false} />
              </Collapse>
              <Collapse
                title="Get All Assets"
                subtitle={
                  <>
                    Can be used to get all existing assets.{" "}
                    <Text>{`{{api}}/assets/all`}</Text>
                  </>
                }
              >
                <GetAllUsersAssets setData={setData} checkOwner={false} />
              </Collapse>
              <Collapse
                title="Delete Asset"
                subtitle={
                  <>
                    Can be used to an existing asset using assetId.{" "}
                    <Text>{`{{api}}/asset/delete/:assetId`}</Text>
                  </>
                }
              >
                <DeleteAsset setData={setData} />
              </Collapse>
              <Collapse
                title="Update Asset"
                subtitle={
                  <>
                    Can be used to update existing asset.{" "}
                    <Text>{`{{api}}/asset/update`}</Text>
                  </>
                }
              >
                <Text>itemDetailsAdd is meant for array keys</Text>
                <Text>
                  if itemDetailsAdd and itemDetailsUpdate have same array key
                  they will merge if the key value is an array
                </Text>
                <Text>Example:</Text>
                <ReactJson src={assetUpdateEdittor} />
                <UpdateAsset setData={setData} />
              </Collapse>
            </Collapse.Group>
          </Collapse>
        </Collapse.Group>
        <Collapse.Group>
          <Collapse title="Lands" subtitle="All apis regarding lands.">
            <Text>Here you will see list of Lands methods</Text>
            <Collapse.Group>
              <Collapse
                title="Create Land"
                subtitle={
                  <>
                    Can be used to create a land.{" "}
                    <Text>{`{{api}}/lands/create`}</Text>
                  </>
                }
              >
                <Text>
                  userId: users id, will be the owner, when dev creating not
                  required
                </Text>
                <Text>
                  assets: array of assets, can hold anything best object
                </Text>
                <Text>Example:</Text>
                <ReactJson src={landEditor} />
                <CreateLand setData={setData} />
              </Collapse>
              <Collapse
                title="Buy Land"
                subtitle={
                  <>
                    Can be used to buy a land.{" "}
                    <Text>{`{{api}}/lands/buy`}</Text>
                  </>
                }
              >
                <Text>Example:</Text>
                <ReactJson src={landBuyEdditor} />
                <BuyLand setData={setData} />
              </Collapse>
              <Collapse
                title="Add Asset To Land"
                subtitle={
                  <>
                    Can be used to add an asset to a land belonging to any user.{" "}
                    <Text>{`{{api}}/lands/assets/add`}</Text>
                  </>
                }
              >
                <Text>landId: land id a unique id</Text>
                <Text>assets: array of assets, can hold anything</Text>
                <Text>Example:</Text>
                <ReactJson src={addAssetsToLandEditor} />
                <AddAssetsToLand setData={setData} />
              </Collapse>
              <Collapse
                title="Get A Users Land"
                subtitle={
                  <>
                    Can be used to get existing account land.{" "}
                    <Text>{`{{api}}/lands/find/:userId/:landId`}</Text>
                  </>
                }
              >
                <GetLand setData={setData} checkOwner={true} />
              </Collapse>
              <Collapse
                title="Get A Land & Bypass Owners"
                subtitle={
                  <>
                    Can be used to get existing land.{" "}
                    <Text>{`{{api}}/lands/find/:landId`}</Text>
                  </>
                }
              >
                <GetLand setData={setData} checkOwner={false} />
              </Collapse>
              <Collapse
                title="Get Users Lands"
                subtitle={
                  <>
                    Can be used to get existing account lands.{" "}
                    <Text>{`{{api}}/lands/all/:userId`}</Text>
                  </>
                }
              >
                <GetUsersLands setData={setData} />
              </Collapse>
              <Collapse
                title="Get Global Lands"
                subtitle={
                  <>
                    Can be used to get all existing lands.{" "}
                    <Text>{`{{api}}/lands/all`}</Text>
                  </>
                }
              >
                <GetGlobalLands setData={setData} />
              </Collapse>
              <Collapse
                title="Delete A Land"
                subtitle={
                  <>
                    Can be used to delete any land.{" "}
                    <Text>{`{{api}}/land/delete/:landId`}</Text>
                  </>
                }
              >
                <DeleteALand setData={setData} />
              </Collapse>
              <Collapse
                title="Update Land"
                subtitle={
                  <>
                    Can be used to update existing land.{" "}
                    <Text>{`{{api}}/land/update`}</Text>
                  </>
                }
              >
                <Text>Example:</Text>
                <UpdateLand setData={setData} />
              </Collapse>
            </Collapse.Group>
          </Collapse>
        </Collapse.Group>
        <Collapse.Group>
          <Collapse title="Products" subtitle="All apis regarding products.">
            <Text>Here you will see list of Products methods</Text>
            <Collapse.Group>
              <Collapse
                title="Add Product"
                subtitle={
                  <>
                    Can be used to add a new product.{" "}
                    <Text>{`{{api}}/products/add`}</Text>
                  </>
                }
              >
                <Text>Example:</Text>
                <ReactJson src={productEditorMain} />
                <AddProduct setData={setData} />
              </Collapse>
              <Collapse
                title="Modify Product"
                subtitle={
                  <>
                    Can be used to modify an existing product.{" "}
                    <Text>{`{{api}}/products/modify`}</Text>
                  </>
                }
              >
                <Text>Example:</Text>
                <ReactJson src={productModifyEditor} />
                <ModifyProduct setData={setData} />
              </Collapse>
              <Collapse
                title="Delete Product"
                subtitle={
                  <>
                    Can be used to delete an existing product.{" "}
                    <Text>{`{{api}}/products/delete/:product_id`}</Text>
                  </>
                }
              >
                <DeleteProduct setData={setData} />
              </Collapse>
              <Collapse
                title="Get A Product"
                subtitle={
                  <>
                    Can be used to get a product.{" "}
                    <Text>{`{{api}}/products/find/:product_id`}</Text>
                  </>
                }
              >
                <GetAProduct setData={setData} />
              </Collapse>
              <Collapse
                title="Get All Products"
                subtitle={
                  <>
                    Can be used to get all products.{" "}
                    <Text>{`{{api}}/products/all`}</Text>
                  </>
                }
              >
                <GetProducts setData={setData} isUser={false} />
              </Collapse>
              <Collapse
                title="Get All Users Products"
                subtitle={
                  <>
                    Can be used to get all users products.{" "}
                    <Text>{`{{api}}/products/all/:userId`}</Text>
                  </>
                }
              >
                <GetProducts setData={setData} isUser={true} />
              </Collapse>
              <Collapse
                title="Buy A Product"
                subtitle={
                  <>
                    Can be used to buy a product.{" "}
                    <Text>{`{{api}}/products/buy/:variant_id?coupon=coupon_here`}</Text>
                  </>
                }
              >
                <BuyProduct setData={setData} />
              </Collapse>
              <Collapse
                title="Add Image To Product"
                subtitle={
                  <>
                    Can be used to buy a product.{" "}
                    <Text>{`{{api}}/products/upload/image/:variant_id`}</Text>
                  </>
                }
              >
                <UploadImageProduct setData={setData} />
              </Collapse>
            </Collapse.Group>
          </Collapse>
        </Collapse.Group>
        <Collapse.Group>
          <Collapse title="Stores" subtitle="All apis regarding stores.">
            <Text>Here you will see list of Stores methods</Text>
            <Collapse.Group>
              <Collapse
                title="Create Store"
                subtitle={
                  <>
                    Can be used to create a new store.{" "}
                    <Text>{`{{api}}/store/create`}</Text>
                  </>
                }
              >
                <Text>Example:</Text>
                <CreateStore setData={setData} />
              </Collapse>
              <Collapse
                title="Get A Store's Products"
                subtitle={
                  <>
                    Can be used to get store products.{" "}
                    <Text>{`{{api}}/store/products/:store_id`}</Text>
                  </>
                }
              >
                <GetStoreProducts setData={setData} />
              </Collapse>
            </Collapse.Group>
          </Collapse>
        </Collapse.Group>
        <Collapse.Group>
          <Collapse
            title="LeaderBoard"
            subtitle="All apis regarding leaderboards."
          >
            <Text>Here you will see list of LeaderBoard methods</Text>
            <Collapse.Group>
              <Collapse
                title="Get LeaderBoards"
                subtitle={
                  <>
                    Can be used to get leaderboards{" "}
                    <Text>{`{{api}}/leaderboard/find`}</Text>
                  </>
                }
              >
                <Text>leaderboard_Type: weekly, monthly, alltime</Text>
                <Text>valueType: time, number</Text>
                <GetLeaderBoards setData={setData} />
              </Collapse>
              <Collapse
                title="Add To LeaderBoard"
                subtitle={
                  <>
                    Can be used to add a user to a leaderboard{" "}
                    <Text b>{`{{api}}/leaderboard/add`}</Text>
                  </>
                }
              >
                <AddToLeaderBoards setData={setData} />
              </Collapse>
              <Collapse
                title="Get Temporary LeaderBoards"
                subtitle={
                  <>
                    Can be used to get temporary leaderboards{" "}
                    <Text>{`{{api}}/leaderboard/temp/{{eventId}}`}</Text>
                  </>
                }
              >
                <GetTempLeaderBoards setData={setData} />
              </Collapse>
              <Collapse
                title="Add To Temporary LeaderBoard"
                subtitle={
                  <>
                    Can be used to add a user to a temporary leaderboard{" "}
                    <Text b>{`{{api}}/leaderboard/temp/add`}</Text>
                  </>
                }
              >
                <AddToTempLeaderBoards setData={setData} />
              </Collapse>
            </Collapse.Group>
          </Collapse>
        </Collapse.Group>
        <Collapse.Group>
          <Collapse title="Server" subtitle="All apis regarding server.">
            <Text>Here you will see list of Server methods</Text>
            <Collapse.Group>
              <Collapse
                title="Add Server"
                subtitle={
                  <>
                    Can be used to add a server{" "}
                    <Text>{`{{api}}/server/add/{{serverAddress}}/{{serverPort}}/{{maxPlayers}}/{{typeOfServer}}/{{serverName}}`}</Text>
                  </>
                }
              >
                <AddToServer setData={setData} />
              </Collapse>
              <Collapse
                title="Join Server"
                subtitle={
                  <>
                    Can be used to join a server{" "}
                    <Text>{`{{api}}/server/join/{{serverAddress}}/{{serverPort}}`}</Text>
                  </>
                }
              >
                <JoinServer setData={setData} />
              </Collapse>
              <Collapse
                title="Leave Server"
                subtitle={
                  <>
                    Can be used to leave a server{" "}
                    <Text>{`{{api}}/server/leave/{{serverAddress}}/{{serverPort}}`}</Text>
                  </>
                }
              >
                <LeaveServer setData={setData} />
              </Collapse>
              <Collapse
                title="Ping Server"
                subtitle={
                  <>
                    Can be used to ping a server{" "}
                    <Text>{`{{api}}/server/ping/{{serverAddress}}/{{serverPort}}`}</Text>
                  </>
                }
              >
                <PingServer setData={setData} />
              </Collapse>
              <Collapse
                title="Lock Server"
                subtitle={
                  <>
                    Can be used to lock a server{" "}
                    <Text>{`{{api}}/server/lock/{{serverAddress}}/{{serverPort}}/true`}</Text>
                  </>
                }
              >
                <LockServer setData={setData} locked={true} />
              </Collapse>
              <Collapse
                title="UnLock Server"
                subtitle={
                  <>
                    Can be used to unlock a server{" "}
                    <Text>{`{{api}}/server/lock/{{serverAddress}}/{{serverPort}}/false`}</Text>
                  </>
                }
              >
                <LockServer setData={setData} locked={false} />
              </Collapse>
              <Collapse
                title="Remove Server"
                subtitle={
                  <>
                    Can be used to remove a server{" "}
                    <Text>{`{{api}}/server/remove/{{serverAddress}}/{{serverPort}}`}</Text>
                  </>
                }
              >
                <RemoveServer setData={setData} />
              </Collapse>
              <Collapse
                title="All Servers"
                subtitle={
                  <>
                    Can be used to get all servers{" "}
                    <Text>{`{{api}}/server/all`}</Text>
                  </>
                }
              >
                <GetAllServers setData={setData} />
              </Collapse>
              <Collapse
                title="Find Server By Server Name"
                subtitle={
                  <>
                    Can be used to find a server by serverName{" "}
                    <Text>{`{{api}}/server/find/{{serverName}}`}</Text>
                  </>
                }
              >
                <FindServerByServerName setData={setData} />
              </Collapse>
              <Collapse
                title="Find Server By Server Type"
                subtitle={
                  <>
                    Can be used to find a server by serverType{" "}
                    <Text>{`{{api}}/server/find/type/{{typeOfServer}}`}</Text>
                  </>
                }
              >
                <FindServersByServerType setData={setData} />
              </Collapse>
            </Collapse.Group>
          </Collapse>
        </Collapse.Group>
        <Collapse.Group>
          <Collapse
            title="Transaction"
            subtitle="All apis regarding transaction."
          >
            <Text>Here you will see list of Transaction methods</Text>
            <Collapse.Group>
              <Collapse
                title="Transaction By Name"
                subtitle={
                  <>
                    <Text>{`{{api}}/transaction/target/:transaction_name`}</Text>
                  </>
                }
              >
                <TransactionActionName setData={setData} />
              </Collapse>
              <Collapse
                title="Transaction Actions"
                subtitle={
                  <>
                    Actions: add, subtract, widthraw, top-up
                    <Text>{`{{api}}/transaction/:action`}</Text>
                  </>
                }
              >
                <TransactionAction setData={setData} />
              </Collapse>
              <Collapse
                title="Get Users Transactions"
                subtitle={
                  <>
                    <Text>{`{{api}}/transaction/user`}</Text>
                  </>
                }
              >
                <GetUsersTransactions setData={setData} />
              </Collapse>
              <Collapse
                title="Get Users Wallet Amount"
                subtitle={
                  <>
                    <Text>{`{{api}}/transaction/user/amount`}</Text>
                  </>
                }
              >
                <GetUsersWalletAmount setData={setData} />
              </Collapse>
            </Collapse.Group>
          </Collapse>
        </Collapse.Group>
        <Collapse.Group>
          <Collapse title="AI" subtitle="All apis regarding AI.">
            <Text>Here you will see list of AI methods</Text>
            <Collapse.Group>
              <Collapse
                title="Create Image Based On Prompt"
                subtitle={"{{api}}/ai/generate/image"}
              >
                <AiImagePrompt setData={setData} />
              </Collapse>
            </Collapse.Group>
          </Collapse>
        </Collapse.Group>
        <Collapse.Group>
          <Collapse title="BillBoard" subtitle="All apis regarding BillBoard.">
            <Text>Here you will see list of BillBoard methods</Text>
            <Collapse.Group>
              <Collapse
                title="Get All Billboards"
                subtitle={"{{api}}/billboard/find/all"}
              >
                <GetAllBillBoard setData={setData} />
              </Collapse>
              <Collapse
                title="Get A Billboard By Name"
                subtitle={"{{api}}/billboard/find/name/:billboardName"}
              >
                <GetABillBoardByName setData={setData} />
              </Collapse>
            </Collapse.Group>
          </Collapse>
        </Collapse.Group>
        <Collapse.Group>
          <Collapse title="Model" subtitle="All apis regarding Models.">
            <Text>Here you will see list of Model zip methods</Text>
            <Collapse.Group>
              <Collapse
                title="Get All Models Zipped"
                subtitle={"{{api}}/model//zip/all/{{forceGetAllCurrentZipped}}"}
              >
                <GetAllZippedModels setData={setData} />
              </Collapse>
            </Collapse.Group>
          </Collapse>
        </Collapse.Group>
      </div>
    </ResultsInfo>
  );
};

export default PlayGround;
